import React from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { AsDiv, CaptionMD, CaptionSM, EyebrowMD, TitleXS } from '@/atoms/Text'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { GuildPromotionCountdown } from './GuildPromotionCountdown'

interface GuildPromotionBannerProps {
  hideNavigation?: boolean
  mobileClassName?: string
}

export const GuildPromotionBanner: React.FC<GuildPromotionBannerProps> = ({
  hideNavigation = false,
  mobileClassName,
}) => {
  const { t } = useTranslate('guild')
  const { sale } = useGuildSignupContext()
  const { isDarkMode } = useThemeContext()

  if (!sale) return null

  return (
    <>
      {/* mobile version */}
      <a href="#plan-selection">
        <div
          className={classNames(
            'absolute inset-x-0 z-20 w-11/12 rounded-2xl mx-auto bg-black/60 md:hidden text-center mb-10',
            {
              'top-3': hideNavigation,
              'top-[64px]': !hideNavigation,
              'border border-x-0 border-core-gray-200': !isDarkMode,
            },
            mobileClassName,
          )}
        >
          <CaptionMD as={AsDiv} weight="bold" color="core-copper" className="px-6 py-3">
            {sale.pitch}
          </CaptionMD>
        </div>
      </a>

      {/* desktop version */}
      <div
        className={classNames('relative z-20 hidden min-h-[88px] w-full bg-white md:block', {
          'top-0': hideNavigation,
          'top-[64px]': !hideNavigation,
          'border border-x-0 border-core-gray-200': !isDarkMode,
        })}
      >
        <Image
          className="absolute inset-y-0 left-0 h-full"
          alt="black friday banner image left"
          width={243}
          height={88}
          src={'/w_243,h_88/v1725044109/angel-studios/shows-left.webp'}
        />
        <Image
          className="absolute inset-y-0 right-0 hidden h-full lg:block"
          alt="black friday banner image right"
          width={243}
          height={88}
          src={'/w_243,h_88/v1725044109/angel-studios/shows-right.webp'}
        />
        <div className="absolute h-full w-full md:block">
          <div className="flex h-full justify-between gap-x-6 pl-64 lg:mx-12 lg:justify-between lg:pr-64">
            <div className="flex shrink-0 flex-col justify-center">
              <div className="flex flex-row items-center gap-2">
                <EyebrowMD weight="bold" className="lg:photon-title-md">
                  {sale.name}
                </EyebrowMD>
              </div>
              {sale.endDate && (
                <>
                  <CaptionSM className="uppercase" weight="bold" color="core-gray-600">
                    <Translate i18nKey="endsIn" t={t}>
                      Ends In
                    </Translate>
                  </CaptionSM>
                  <GuildPromotionCountdown endDate={sale.endDate} />
                </>
              )}
            </div>
            <div className="flex items-center justify-center">
              <TitleXS color="core-gray-600" weight="bold">
                {sale.pitch}
              </TitleXS>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
